import { useState } from 'react';

interface YesNoQuestion {
  id: string;
  question: string;
}

interface YesNoQuestionsProps {
  questions: YesNoQuestion[];
  onSubmit: (answers: { questionId: string; answer: boolean }[]) => void;
  messages: {
    heading: string;
    subheading: string;
    submitButton: string;
  };
}

export default function YesNoQuestions({ questions, onSubmit, messages }: YesNoQuestionsProps) {
  const [answers, setAnswers] = useState<{ [key: string]: boolean | null }>(
    questions.reduce((acc, q) => ({ ...acc, [q.id]: null }), {})
  );

  const handleAnswer = (questionId: string, answer: boolean) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));
  };

  const handleSubmit = () => {
    const allAnswered = questions.every((q) => answers[q.id] !== null);
    if (!allAnswered) return;

    const answerArray = questions.map((q) => ({
      questionId: q.id,
      answer: answers[q.id] as boolean,
    }));

    onSubmit(answerArray);
  };

  const allAnswered = questions.every((q) => answers[q.id] !== null);

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-light/30 to-brand-light/60 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8">
        <h1 className="text-3xl font-bold font-heading text-brand-dark mb-2 text-center">
          {messages.heading}
        </h1>
        <p className="text-brand-dark/70 mb-8 text-center">{messages.subheading}</p>

        <div className="space-y-6">
          {questions.map((question, index) => (
            <div key={question.id} className="border-b border-brand-green/20 pb-6 last:border-0">
              <p className="text-lg font-medium text-brand-dark mb-4">
                {index + 1}. {question.question}
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => handleAnswer(question.id, true)}
                  className={`flex-1 py-3 px-6 rounded-lg font-medium transition transform active:scale-95 ${
                    answers[question.id] === true
                      ? 'bg-brand-green text-brand-dark shadow-lg'
                      : 'bg-brand-light/50 text-brand-dark hover:bg-brand-light/70'
                  }`}
                >
                  Yes
                </button>
                <button
                  onClick={() => handleAnswer(question.id, false)}
                  className={`flex-1 py-3 px-6 rounded-lg font-medium transition transform active:scale-95 ${
                    answers[question.id] === false
                      ? 'bg-brand-green text-brand-dark shadow-lg'
                      : 'bg-brand-light/50 text-brand-dark hover:bg-brand-light/70'
                  }`}
                >
                  No
                </button>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={handleSubmit}
          disabled={!allAnswered}
          className={`w-full mt-8 py-4 px-6 rounded-lg font-semibold text-lg transition transform active:scale-95 ${
            allAnswered
              ? 'bg-brand-green text-brand-dark hover:opacity-90 shadow-lg'
              : 'bg-gray-300 text-brand-dark/40 cursor-not-allowed'
          }`}
        >
          {messages.submitButton}
        </button>
      </div>
    </div>
  );
}
